import { Dispatch, SetStateAction, useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import { Modal, Button, Select, MenuItem, SelectChangeEvent, FormHelperText } from "@mui/material";
import React from "react";
import { useGetToppingListQuery, useCreateYogurtToppingMutation, useUpdateYogurtToppingMutation, YogurtToppingItem } from "../store/api/commonAPI";
import { showToast, showError } from "../constants/toasts";
import Loader from "../constants/Loader";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  yogurtId?: string;
  editTopping?: YogurtToppingItem | null;
}

export default function AddTypeTopping({
  open,
  onClose,
  setOpen,
  yogurtId = "",
  editTopping,
}: ModalProps) {
  const [toppingId, setToppingId] = useState<string>("");
  const [pricing, setPricing] = useState<string>("");
  const [errors, setErrors] = useState<{
    toppingId?: string;
    pricing?: string;
  }>({});

  const isEditMode = !!editTopping;

  // Fetch toppings list for dropdown
  const dropdownPayload = {
    page: 1,
    limit: 100, // Get all toppings
    yogurtId: yogurtId, // Filter by yogurt ID
  };
  console.log("📋 Topping Dropdown@@@@@@@@@@ API Payload:", dropdownPayload);
  const { data: toppingsData, refetch: refetchToppings } = useGetToppingListQuery(dropdownPayload);

  // Create and update yogurt topping mutations
  const [createYogurtTopping, { isLoading: isCreating }] = useCreateYogurtToppingMutation();
  const [updateYogurtTopping, { isLoading: isUpdating }] = useUpdateYogurtToppingMutation();

  const isLoading = isCreating || isUpdating;

  // Extract toppings from API response
  const toppings = React.useMemo(() => {
    if (toppingsData?.data?.topping && Array.isArray(toppingsData.data.topping)) {
      return toppingsData.data.topping.filter((topping: any) => topping.isActive !== false);
    }
    return [];
  }, [toppingsData]);

  // Refetch dropdown list when modal opens
  useEffect(() => {
    if (open) {
      refetchToppings();
    }
  }, [open, refetchToppings]);

  // Reset form when modal opens/closes or when editTopping changes
  useEffect(() => {
    if (!open) {
      setToppingId("");
      setPricing("");
      setErrors({});
    } else if (editTopping) {
      // Populate form with edit data
      const toppingDetailsId = editTopping.toppingId || editTopping.toppingDetails?._id || "";
      setToppingId(toppingDetailsId);
      setPricing((editTopping.pricing || editTopping.price || 0).toString());
      setErrors({});
    } else {
      // Reset for add mode
      setToppingId("");
      setPricing("");
      setErrors({});
    }
  }, [open, editTopping]);

  const handleToppingChange = (event: SelectChangeEvent) => {
    const value = event.target.value as string;
    setToppingId(value);
    if (errors.toppingId) {
      setErrors((prev) => ({ ...prev, toppingId: undefined }));
    }
  };

  const handlePricingChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setPricing(value);
    if (errors.pricing) {
      setErrors((prev) => ({ ...prev, pricing: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { toppingId?: string; pricing?: string } = {};

    // Validate topping selection
    if (!toppingId || toppingId === "0") {
      newErrors.toppingId = "Please select a topping";
    }

    // Validate pricing
    if (!pricing.trim()) {
      newErrors.pricing = "Price is required";
    } else {
      const price = parseFloat(pricing);
      if (isNaN(price) || price < 0) {
        newErrors.pricing = "Price must be a positive number";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!yogurtId) {
      showError("Yogurt ID is missing. Please try again.");
      return;
    }

    try {
      let response;
      
      if (isEditMode && editTopping) {
        // Update existing yogurt topping - only send fields that can be edited
        const payload = {
          id: editTopping._id || editTopping.id || "",
          pricing: parseFloat(pricing),
          isActive: editTopping.isActive ?? true,
        };

        console.log("📤 Updating yogurt topping with payload:", payload);
        response = await updateYogurtTopping(payload).unwrap();
        console.log("✅ Update yogurt topping response:", response);
      } else {
        // Create new yogurt topping - include all required fields
        const payload = {
          yogurtId: yogurtId,
          toppingId: toppingId,
          pricing: parseFloat(pricing),
        };

        console.log("📤 Creating yogurt topping with payload:", payload);
        response = await createYogurtTopping(payload).unwrap();
        console.log("✅ Create yogurt topping response:", response);
      }

      // Show success message from API response or default message
      const selectedTopping = toppings.find((t: any) => (t._id || t.id) === toppingId);
      const toppingName = selectedTopping?.name || editTopping?.toppingDetails?.name || "topping";
      const successMessage = response?.message || `Yogurt topping "${toppingName}" has been ${isEditMode ? "updated" : "added"} successfully`;
      showToast(successMessage);

      // Refetch dropdown list to get latest data
      refetchToppings();

      // Reset form and close modal
      setToppingId("");
      setPricing("");
      setErrors({});
      setOpen(false);
      onClose(); // Call onClose to trigger refetch in parent
    } catch (error: any) {
      console.error(`Error ${isEditMode ? "updating" : "creating"} yogurt topping:`, error);

      // Extract error message from API response or show default
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.data?.error ||
        error?.message ||
        `Failed to ${isEditMode ? "update" : "add"} yogurt topping. Please try again.`;

      showError(errorMessage);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setToppingId("");
      setPricing("");
      setErrors({});
      setOpen(false);
      onClose();
    }
  };

  return (
    <>
      <Loader isLoad={isLoading} />
      <Modal
        className="modal addType_modal"
        open={open}
        onClose={handleClose}
      >
        <div className="modal-dialog">
          <div className="modal-body">
            <div className="modal_title">
              <h2>{isEditMode ? "Edit Topping" : "Add Topping"}</h2>
              <button
                type="button"
                className="btn-close"
                onClick={handleClose}
                aria-label="Close"
                disabled={isLoading}
              >
                <CloseIcon />
              </button>
            </div>

            <form className="form" onSubmit={handleSubmit}>
              <div className="control_group">
                <label htmlFor="select-topping">
                  {isEditMode ? "Topping" : "Select Topping"}
                  {!isEditMode && <span className="req">*</span>}
                </label>
                {isEditMode ? (
                  <TextField
                    fullWidth
                    hiddenLabel
                    id="select-topping"
                    value={editTopping?.toppingDetails?.name || "N/A"}
                    disabled
                    InputProps={{
                      readOnly: true,
                      sx: {
                        color: '#000000',
                        '& .MuiInputBase-input.Mui-disabled': {
                          WebkitTextFillColor: '#000000',
                          color: '#000000',
                        },
                      },
                    }}
                    sx={{
                      '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: '#000000',
                        color: '#000000',
                      },
                    }}
                  />
                ) : (
                  <>
                    <Select
                      fullWidth
                      hiddenLabel
                      labelId="select-topping-label"
                      id="select-topping"
                      value={toppingId}
                      onChange={handleToppingChange}
                      error={!!errors.toppingId}
                      disabled={isLoading}
                    >
                      <MenuItem value="0" disabled>
                        Choose a topping
                      </MenuItem>
                      {toppings.map((topping: any) => (
                        <MenuItem key={topping._id || topping.id} value={topping._id || topping.id}>
                          {topping.name || "N/A"}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.toppingId && (
                      <FormHelperText error>{errors.toppingId}</FormHelperText>
                    )}
                  </>
                )}
              </div>

              <div className="control_group">
                <label htmlFor="price-input">
                  Price (OMR)<span className="req">*</span>
                </label>
                <TextField
                  fullWidth
                  hiddenLabel
                  id="price-input"
                  placeholder="0.00"
                  type="number"
                  value={pricing}
                  onChange={handlePricingChange}
                  error={!!errors.pricing}
                  disabled={isLoading}
                  inputProps={{
                    min: 0,
                    step: 0.01,
                  }}
                />
                {errors.pricing && (
                  <FormHelperText error>{errors.pricing}</FormHelperText>
                )}
              </div>

              <div className="footer_btn_flex">
                <Button
                  type="submit"
                  className="btn btn_primary"
                  disabled={isLoading}
                >
                  {isLoading ? (isEditMode ? "Updating..." : "Adding...") : (isEditMode ? "Update Topping" : "Add Topping")}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </Modal>
    </>
  );
}
